import React, { useMemo, useState, useEffect } from "react";
import {
  DataGrid as MuiDataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
  GridPaginationModel,
} from "@mui/x-data-grid";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

const StyledDataGrid = styled(MuiDataGrid)<{ headerBgColor?: string }>(
  ({ theme, headerBgColor = "#F7F7F7" }) => ({
    // Root styles
    "&.MuiDataGrid-root": {
      "--unstable_DataGrid-overlayBackground": "transparent",
      "--DataGrid-containerBackground": headerBgColor,
      "--DataGrid-rowHeight": "56px",
      "--DataGrid-headerHeight": "56px",
      "--DataGrid-rowSpacing": "0px",
      "--DataGrid-columnHeaderBackgroundColor": headerBgColor,
    },

    // Header styles
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "var(--DataGrid-columnHeaderBackgroundColor)",
      borderRadius: 0,
      minHeight: "var(--DataGrid-headerHeight) !important",
      maxHeight: "var(--DataGrid-headerHeight) !important",
    },

    "& .MuiDataGrid-columnHeader": {
      padding: "0 16px",
      backgroundColor: "var(--DataGrid-columnHeaderBackgroundColor) !important",
      "&:focus, &:focus-within": {
        outline: "none",
      },
    },

    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 400, // Normal weight for header text
      color: "#000",
      fontSize: "0.75rem", // Smaller font size for better fit
      lineHeight: "1.25",
      whiteSpace: "normal",
      textOverflow: "clip",
      wordBreak: "break-word",
      textAlign: "center",
    },

    // Header container styles
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0",
      "&.MuiDataGrid-columnHeader--alignCenter": {
        justifyContent: "center",
      },
    },

    "& .MuiDataGrid-columnHeaderTitleContainerContent": {
      width: "100%",
    },

    // Cell styles
    "& .MuiDataGrid-cell": {
      padding: "0 16px",
      display: "flex",
      alignItems: "center",
      "&:focus, &:focus-within": {
        outline: "none",
      },
      "& > div": {
        width: "100%",
      },
    },

    // Row styles
    "& .MuiDataGrid-row": {
      minHeight: "56px !important",
      maxHeight: "none !important",
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.04)",
      },
      "&.Mui-selected": {
        backgroundColor: "transparent",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      },
    },

    // Column header and cell spacing
    "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
      "&:first-of-type": {
        paddingLeft: "24px",
      },
      "&:last-child": {
        paddingRight: "24px",
      },
    },

    // Hide column separators
    "& .MuiDataGrid-columnSeparator": {
      display: "none",
    },

    // Scrollbar styling
    "& .MuiDataGrid-virtualScroller": {
      "&::-webkit-scrollbar": {
        height: "8px",
        width: "8px",
      },
      "&::-webkit-scrollbar-track": {
        background: "#f1f1f1",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#888",
        borderRadius: "4px",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: "#555",
      },
    },

    // Hide loading progress
    "& .MuiLinearProgress-root": {
      display: "none !important",
    },

    // Center-aligned cells
    "& .MuiDataGrid-cell--textCenter": {
      justifyContent: "center",
      textAlign: "center",
    },
  }),
);

interface DataGridProps {
  columns: GridColDef[];
  rows: any[];
  loading?: boolean;
  onRowClick?: (params: any) => void;
  getRowId?: (row: any) => string | number;
  pageSizeOptions?: number[];
  initialState?: any;
  slots?: {
    toolbar?: any;
    [key: string]: any;
  };
  slotProps?: any;
  headerBgColor?: string;
  hideFooter?: boolean;
  paginationModel?: GridPaginationModel;
  onPaginationModelChange?: (model: GridPaginationModel) => void;
}

const DataGrid: React.FC<DataGridProps> = ({
  columns,
  rows,
  loading = false,
  onRowClick,
  getRowId = (row) => row.id,
  pageSizeOptions = [10, 20, 50, 100],
  initialState = {
    pagination: {
      paginationModel: {
        pageSize: 20, // Default to 20 rows per page
        page: 0,
      },
    },
  },
  slots = {},
  slotProps = {},
  hideFooter = false,
  paginationModel: externalPaginationModel,
  onPaginationModelChange: externalOnPaginationModelChange,
  ...props
}) => {
  // Internal pagination state
  const [internalPaginationModel, setInternalPaginationModel] =
    useState<GridPaginationModel>({
      page: initialState.pagination?.paginationModel?.page || 0,
      pageSize: initialState.pagination?.paginationModel?.pageSize || 20,
    });

  // Use external pagination if provided, otherwise use internal
  const paginationModel = externalPaginationModel || internalPaginationModel;
  const onPaginationModelChange =
    externalOnPaginationModelChange || setInternalPaginationModel;

  // Update internal state when external state changes
  useEffect(() => {
    if (externalPaginationModel) {
      setInternalPaginationModel(externalPaginationModel);
    }
  }, [externalPaginationModel]);

  const enhancedColumns = useMemo(() => {
    return columns.map((col) => ({
      ...col,
      headerClassName: "data-grid-header",
      cellClassName: "data-grid-cell",
      flex: col.flex === undefined ? 1 : col.flex,
    }));
  }, [columns]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        "& .MuiDataGrid-main": {
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
        "& .MuiDataGrid-virtualScroller": {
          flexGrow: 1,
          overflow: "auto",
        },
        "& .MuiDataGrid-footerContainer": {
          minHeight: "52px", // Ensure enough space for pagination
          borderTop: "1px solid rgba(224, 224, 224, 1)",
          "& .MuiTablePagination-toolbar": {
            minHeight: "52px",
          },
        },
      }}
    >
      <StyledDataGrid
        headerBgColor={props.headerBgColor}
        rows={rows}
        columns={enhancedColumns}
        loading={loading}
        autoHeight={false}
        disableRowSelectionOnClick
        pageSizeOptions={pageSizeOptions}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pagination
        paginationMode="client"
        getRowId={getRowId}
        onRowClick={onRowClick}
        disableColumnMenu
        disableDensitySelector
        disableColumnSelector
        disableColumnFilter
        disableColumnResize
        disableMultipleRowSelection
        hideFooter={hideFooter}
        sx={{
          "& .MuiDataGrid-columnHeader": {
            backgroundColor:
              "var(--DataGrid-columnHeaderBackgroundColor) !important",
          },
        }}
        slots={{
          toolbar: hideFooter ? null : GridToolbar,
          ...slots,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
          },
          ...slotProps,
        }}
        {...props}
      />
    </Box>
  );
};

export default DataGrid;
